'use client';

import React, { useEffect, useState } from 'react';
import { motion, Variants } from 'motion/react';
import { cn } from '../lib/utils';

// Hook for SSR safety
function useIsomorphicLayoutEffect(effect: React.EffectCallback, deps?: React.DependencyList) {
  const isClient = typeof window !== 'undefined';
  const useLayoutEffect = isClient ? React.useLayoutEffect : React.useEffect;
  useLayoutEffect(effect, deps);
}

// Hook to safely use motion components
function useMotionSafe() {
  const [isMounted, setIsMounted] = useState(false);
  
  useIsomorphicLayoutEffect(() => {
    setIsMounted(true);
  }, []);
  
  return isMounted;
}

// Reusable animation variants
export const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 70 } as const
  }
};

export const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { type: "spring" } as const
  }
};

export const listItem = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { type: "spring" } as const
  }
};

export const floatingButton = {
  animate: {
    y: [0, -8, 0],
    transition: {
      repeat: Infinity,
      repeatType: "reverse",
      duration: 1.5,
      ease: "easeInOut" as const
    } as const
  }
};

export const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { type: "spring", stiffness: 100 } as const
  }
};

export const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 80 } as const,
  },
};

export const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } as const },
};

export const cardHoverVariants = {
  hover: {
    y: -5,
    transition: { duration: 0.3, type: "spring" } as const,
  },
};

export const buttonVariants = {
  idle: { scale: 1 },
  hover: {
    scale: 1.05,
    boxShadow: "0px 8px 15px rgba(186, 51, 255, 0.12)",
    transition: { duration: 0.3, type: "spring", stiffness: 400 } as const,
  },
  tap: { scale: 0.95 },
};

export const arrowVariants = {
  idle: { x: 0 },
  hover: {
    x: 5,
    transition: { repeat: Infinity, repeatType: "reverse", duration: 0.6 } as const,
  },
};

// Animated components with SSR safety
export const AnimatedSection = ({ 
  children, 
  className, 
  variants, 
  initial = "hidden", 
  animate = "visible", 
  ...props 
}: React.ComponentProps<typeof motion.div> & { children: React.ReactNode }) => {
  const isMotionSafe = useMotionSafe();
  
  if (!isMotionSafe) {
    return <div className={className} {...(props as any)}>{children}</div>;
  }
  
  return (
    <motion.div
      variants={variants}
      initial={initial}
      animate={animate}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
};

export const AnimatedCard = ({ 
  children, 
  className, 
  ...props 
}: React.ComponentProps<typeof motion.div> & { children: React.ReactNode }) => {
  const isMotionSafe = useMotionSafe();
  
  if (!isMotionSafe) {
    return <div className={cn('hover-lift', className)} {...(props as any)}>{children}</div>;
  }
  
  return (
    <motion.div
      whileHover={{ y: -5 }}
      className={cn('hover-lift', className)}
      {...props}
    >
      {children}
    </motion.div>
  );
};

export const AnimatedButton = ({ 
  children, 
  className,
  floating = false,
  ...props 
}: React.ComponentProps<typeof motion.div> & { 
  children: React.ReactNode;
  floating?: boolean;
}) => {
  const isMotionSafe = useMotionSafe();
  
  if (!isMotionSafe) {
    return (
      <div
        className={cn(
          'transition-transform duration-300',
          floating && 'animate-bounce',
          className
        )}
        {...(props as any)}
      >
        {children}
      </div>
    );
  }
  
  return (
    <motion.div
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className={cn(
        'transition-transform duration-300',
        floating && 'animate-bounce',
        className
      )}
      {...props}
    >
      {children}
    </motion.div>
  );
};

export const AnimatedFeature = ({ 
  children, 
  className,
  index = 0,
  ...props 
}: React.ComponentProps<typeof motion.div> & { 
  children: React.ReactNode;
  index?: number;
}) => {
  const isMotionSafe = useMotionSafe();
  
  if (!isMotionSafe) {
    return (
      <div
        className={cn(
          'animate-fade-in-up hover-lift',
          className
        )}
        {...(props as any)}
      >
        {children}
      </div>
    );
  }
  
  return (
    <motion.div
      variants={fadeInUp}
      className={cn(
        'animate-fade-in-up hover-lift',
        className
      )}
      {...props}
    >
      {children}
    </motion.div>
  );
};

export const AnimatedIcon = ({ 
  children, 
  className,
  index = 0,
  ...props 
}: React.ComponentProps<typeof motion.div> & { 
  children: React.ReactNode;
  index?: number;
}) => {
  const isMotionSafe = useMotionSafe();
  
  if (!isMotionSafe) {
    return (
      <div
        className={cn(
          'animate-pop-in',
          index === 1 && 'delay-100',
          index === 2 && 'delay-200',
          index === 3 && 'delay-300',
          index === 4 && 'delay-400',
          index === 5 && 'delay-500',
          className
        )}
        {...(props as any)}
      >
        {children}
      </div>
    );
  }

  return (
    <motion.div
      initial={{ scale: 0.8 }}
      animate={{ scale: 1 }}
      transition={{ delay: 0.2 + index * 0.1, type: "spring" } as const}
      className={cn(
        'animate-pop-in',
        index === 1 && 'delay-100',
        index === 2 && 'delay-200',
        index === 3 && 'delay-300',
        index === 4 && 'delay-400',
        index === 5 && 'delay-500',
        className
      )}
      {...props}
    >
      {children}
    </motion.div>
  );
};

export const AnimatedListItem = ({ 
  children, 
  className,
  delay = 0,
  hoverEffect = false,
  ...props 
}: React.ComponentProps<typeof motion.li> & { 
  children: React.ReactNode;
  delay?: number;
  hoverEffect?: boolean;
}) => {
  const isMotionSafe = useMotionSafe();
  
  if (!isMotionSafe) {
    return (
      <li
        className={cn(
          'animate-fade-in-right',
          delay === 0.1 && 'delay-100',
          delay === 0.2 && 'delay-200',
          delay === 0.3 && 'delay-300',
          delay === 0.4 && 'delay-400',
          delay === 0.5 && 'delay-500',
          hoverEffect && 'hover:translate-x-1 transition-transform duration-300',
          className
        )}
        {...(props as any)}
      >
        {children}
      </li>
    );
  }

  return (
    <motion.li
      variants={listItem}
      initial="hidden"
      animate="visible"
      transition={{ delay }}
      className={cn(
        'animate-fade-in-right',
        delay === 0.1 && 'delay-100',
        delay === 0.2 && 'delay-200',
        delay === 0.3 && 'delay-300',
        delay === 0.4 && 'delay-400',
        delay === 0.5 && 'delay-500',
        hoverEffect && 'hover:translate-x-1 transition-transform duration-300',
        className
      )}
      {...props}
    >
      {children}
    </motion.li>
  );
}; 